import React, { useState, useEffect, useMemo } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import PropTypes from 'prop-types';

import { noop } from '@adsk/alloy-react-helpers';

import Table, { TABLE_SIZES } from '../../sheets/components/Table';
import { getAllDisabledDocuments } from '../helpers/formatEntities';
import { getListViewCellRenderer } from '../helpers/getListViewCellRenderer';
import { processTreeViewEntities } from '../helpers/processTreeViewEntities';

const columnHelper = createColumnHelper();

const ListView = ({
  loading,
  error,
  multiple,
  onChange,
  disabledIds = [],
  selectedIds = [],
  entities,
  setSelection,
  onLoadMore,
  onTryAgain,
  dataViewProps = {},
}) => {
  const {
    dataFilter,
    allowWholeFolderSelection = false,
    implicitlySelectedIds = [],
    setImplicitlySelectedIds = noop,
    rowIdsWithIndicator = [],
    renderIndicator = noop,
  } = dataViewProps;
  const data = useMemo(
    () => processTreeViewEntities(entities, dataFilter),
    [dataFilter, entities],
  );

  const [allDisabledIds, setAllDisableIds] = useState(disabledIds);

  const getIdsToImplicitlySelect = React.useCallback(
    (latestSelectedIds) => {
      const idsToBeImplicitlySelected = [];

      entities.forEach((entity) => {
        let ancestralIds;
        if ('folderType' in entity) {
          // Folder
          ancestralIds = entity.parents.map((parent) => parent.urn);
        } else {
          // File
          ancestralIds = [
            entity.parentFolderUrn,
            ...entity.parentFolder.parents.map((parent) => parent.urn),
          ];
        }

        const isEntityImplicitlySelected = !!ancestralIds.find(
          (ancestralId) =>
            !!latestSelectedIds.find(
              (selectedId) => selectedId === ancestralId,
            ),
        );

        isEntityImplicitlySelected &&
          idsToBeImplicitlySelected.push(entity.urn);
      });

      return idsToBeImplicitlySelected;
    },
    [entities],
  );

  // Reset upon each CustomDataView render
  useEffect(() => setImplicitlySelectedIds([]), [setImplicitlySelectedIds]);

  // Implicitly select on each re-render of the data list
  useEffect(() => {
    if (!allowWholeFolderSelection) return;

    setImplicitlySelectedIds(getIdsToImplicitlySelect(selectedIds));
  }, [
    selectedIds,
    allowWholeFolderSelection,
    setImplicitlySelectedIds,
    getIdsToImplicitlySelect,
  ]);

  useEffect(() => {
    const retArray = getAllDisabledDocuments(data);
    // If no disabled documents were returned, use disableIds array as-is.
    // to avoid pointless renders.
    setAllDisableIds(
      retArray.length
        ? [...new Set([...disabledIds, ...retArray])]
        : disabledIds,
    );
  }, [disabledIds, data]);

  const handleOnChange = React.useCallback(
    ({ entity, checked }) => {
      onChange({
        entity,
        checked,
      });

      if (allowWholeFolderSelection) {
        /**
         * Determine any explicitly selected ids that's to be implicitly selected
         * and remove from the explicitly selectedIds list
         */
        const latestSelectedIds = [...selectedIds, entity.urn];
        const idsToImplicitlySelect =
          getIdsToImplicitlySelect(latestSelectedIds);

        latestSelectedIds.forEach((selectedId) => {
          const selectedIdToBeRemoved = idsToImplicitlySelect.find(
            (id) => id === selectedId,
          );

          if (selectedIdToBeRemoved) {
            onChange({
              entity: entities.find(
                (entity) => entity.urn === selectedIdToBeRemoved,
              ),
              checked: false,
            });
          }
        });
      }
    },
    [
      onChange,
      allowWholeFolderSelection,
      entities,
      getIdsToImplicitlySelect,
      selectedIds,
    ],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('file', {
        id: 'file',
        header: 'file',
        minSize: 200,
        enableResizing: false,
        /* eslint-disable react/prop-types */
        cell: (props) => {
          const rowData = props.row.original;
          const showIndicator = rowIdsWithIndicator.find(
            (rowId) => rowId === rowData.urn,
          );

          return getListViewCellRenderer(
            rowData,
            [...allDisabledIds, ...implicitlySelectedIds],
            showIndicator && renderIndicator(),
          );
        },
        /* eslint-enable react/prop-types */
      }),
    ],
    [
      rowIdsWithIndicator,
      allDisabledIds,
      implicitlySelectedIds,
      renderIndicator,
    ],
  );

  return (
    <Table
      hideHeader // Hide table header
      tableSize={TABLE_SIZES.LARGE}
      columns={columns}
      entities={data}
      disabledIds={[...allDisabledIds, ...implicitlySelectedIds]}
      selectedIds={[...selectedIds, ...implicitlySelectedIds]}
      multiple={multiple}
      onLoadMore={onLoadMore}
      onChange={handleOnChange}
      loading={loading}
      error={error}
      onTryAgain={onTryAgain}
      setSelection={setSelection}
    />
  );
};

ListView.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  entities: PropTypes.array.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  setSelection: PropTypes.func,
  dataViewProps: PropTypes.shape({
    dataFilter: PropTypes.func,
    enableFoldersSelection: PropTypes.bool,
    allowWholeFolderSelection: PropTypes.bool,
    rowIdsWithIndicator: PropTypes.array,
    renderIndicator: PropTypes.func,
  }),
  onChange: PropTypes.func,
  onLoadMore: PropTypes.func,
  onTryAgain: PropTypes.func,
};

export default ListView;
